import { Directive, ElementRef, input, AfterViewInit, OnDestroy, NgZone } from '@angular/core';

@Directive({
  selector: '[msAutoFitText]',
  standalone: true
})
export class AutoFitTextDirective implements AfterViewInit, OnDestroy {
  /** Maximum lines allowed before font begins shrinking (default: 1) */
  public msAutoFitMaxLines = input(1);
  /** Minimum font size in pixels before truncation kicks in (default: 16) */
  public msAutoFitMinFontSize = input(16);

  private resizeObserver: ResizeObserver | null = null;
  private mutationObserver: MutationObserver | null = null;

  constructor(private el: ElementRef<HTMLElement>, private zone: NgZone) {}

  public ngAfterViewInit(): void {
    this.zone.runOutsideAngular(() => {
      this.fitText();

      // Observe parent for container width changes (avoids feedback loop from self-resize)
      this.resizeObserver = new ResizeObserver(() => this.fitText());
      const parent = this.el.nativeElement.parentElement;
      if (parent) {
        this.resizeObserver.observe(parent);
      }

      // Observe element for content changes (e.g. async data loading)
      this.mutationObserver = new MutationObserver(() => this.fitText());
      this.mutationObserver.observe(this.el.nativeElement, {
        childList: true,
        characterData: true,
        subtree: true
      });
    });
  }

  public ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.mutationObserver?.disconnect();
  }

  private fitText(): void {
    const el = this.el.nativeElement;
    if (!el.textContent?.trim()) return;

    const maxLines = this.msAutoFitMaxLines();
    const rawMinSize = this.msAutoFitMinFontSize();

    // Temporarily remove inline overrides to read the CSS-defined font size
    const savedFontSize = el.style.fontSize;
    el.style.fontSize = '';
    const originalSize = parseFloat(getComputedStyle(el).fontSize);
    el.style.fontSize = savedFontSize;

    if (!originalSize) return;

    // Clamp minSize so it never exceeds the CSS-defined font size
    const minSize = Math.min(rawMinSize, originalSize);

    // Reset to original size and clear any previous truncation
    el.style.fontSize = `${originalSize}px`;
    el.style.display = '';
    el.style.setProperty('-webkit-line-clamp', '');
    el.style.setProperty('-webkit-box-orient', '');
    el.style.overflow = '';
    el.style.textOverflow = '';

    // If it already fits, remove inline override and bail
    if (this.fitsInLines(maxLines)) {
      el.style.fontSize = '';
      return;
    }

    // Binary search for the largest font size that still fits
    let lo = minSize;
    let hi = originalSize;

    while (hi - lo > 0.5) {
      const mid = (lo + hi) / 2;
      el.style.fontSize = `${mid}px`;
      if (this.fitsInLines(maxLines)) {
        lo = mid;
      } else {
        hi = mid;
      }
    }

    el.style.fontSize = `${lo}px`;

    // If still overflows at minimum size, clamp to maxLines with ellipsis
    if (!this.fitsInLines(maxLines)) {
      el.style.display = '-webkit-box';
      el.style.setProperty('-webkit-line-clamp', `${maxLines}`);
      el.style.setProperty('-webkit-box-orient', 'vertical');
      el.style.overflow = 'hidden';
      el.style.textOverflow = 'ellipsis';
    }
  }

  private fitsInLines(maxLines: number): boolean {
    const el = this.el.nativeElement;
    const computed = getComputedStyle(el);
    const lineHeight = parseFloat(computed.lineHeight) || parseFloat(computed.fontSize) * 1.2;
    return el.scrollHeight <= lineHeight * maxLines + 2;
  }
}
